'use client';

import { LinkButton } from '@/components/ui/Button/LinkButton';
import { Icon } from '@/components/ui/Icon';
import { getBoutiqueFindCtaDefaults } from '@/lib/boutique/constants';
import { useTranslations } from 'next-intl';

import styles from './BoutiquesFindCta.module.scss';

const { storeLocatorUrl: DEFAULT_STORE_LOCATOR_URL, imageUrl: DEFAULT_IMAGE_URL } =
  getBoutiqueFindCtaDefaults();

export interface BoutiquesFindCtaProps {
  /** External store locator URL. */
  href?: string;
  imageUrl?: string;
  imageAlt?: string;
}

/**
 * Full-width CTA shown below the boutiques grid (copy + outlined button, lifestyle image).
 */
export function BoutiquesFindCta({
  href = DEFAULT_STORE_LOCATOR_URL,
  imageUrl = DEFAULT_IMAGE_URL,
  imageAlt,
}: Readonly<BoutiquesFindCtaProps>) {
  const t = useTranslations('boutiquesPage');

  return (
    <section className={styles.section} aria-labelledby="boutiques-find-cta-heading">
      <h2 id="boutiques-find-cta-heading" className={styles.visuallyHidden}>
        {t('findBoutiqueHeading')}
      </h2>

      <div className={styles.panel}>
        <div className={styles.content}>
          <p className={styles.description}>{t('findBoutiqueDescription')}</p>
          <LinkButton
            href={href}
            variant="outline"
            size="md"
            external
            className={styles.cta}
            iconAfter={<Icon name="chevron-right" size={14} aria-hidden="true" />}
          >
            {t('findBoutiqueCta')}
          </LinkButton>
        </div>

        <div className={styles.media}>
          <img
            src={imageUrl}
            alt={imageAlt ?? t('findBoutiqueImageAlt')}
            className={styles.image}
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </section>
  );
}

BoutiquesFindCta.displayName = 'BoutiquesFindCta';
